import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { CartItem, Product } from '@/types';
import { CartService, AddToCartRequest, CartData, CartItemData } from '@/services/cart.service';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface CartContextType {
  cart: CartItem[];
  cartData: CartData | null;
  addToCart: (product: Product) => Promise<void>;
  removeFromCart: (productId: number) => Promise<void>;
  updateQuantity: (productId: number, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  isCartOpen: boolean;
  setIsCartOpen: (isOpen: boolean) => void;
  isLoading: boolean;
  error: string | null;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_ID_KEY = 'indispare_cart_id';

// Utility function to get cart ID from localStorage
export const getStoredCartId = (): string | null => {
  return localStorage.getItem(CART_ID_KEY);
};

// Utility function to clear cart ID from localStorage
export const clearStoredCartId = (): void => {
  localStorage.removeItem(CART_ID_KEY);
};

const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartData, setCartData] = useState<CartData | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();

  // Convert API cart data to local CartItem format
  const convertCartDataToCartItems = (cartData: CartData): CartItem[] => {
    console.log('Converting cart data to cart items:', cartData);
    return cartData.items.map((item: CartItemData) => ({
      id: item.id, // Include cart item ID from API
      product: {
        id: item.product.id,
        product_id: item.product.id,
        name: item.product.name,
        sku: item.sku, // Use sku from cart item, not product
        url_key: item.product.url_key,
        price: parseFloat(item.product.price),
        special_price: null,
        selling_price_discount: 0,
        final_price: parseFloat(item.product.price),
        short_description: item.product.name,
        description: item.product.name,
        weight: '1',
        status: 1,
        visible_individually: 1,
        meta_title: item.product.name,
        meta_keywords: '',
        meta_description: '',
        category_id: 1,
        total_orders: 0,
        category: {
          id: 1,
          name: 'General',
          slug: 'general',
          url_path: 'general',
          description: '',
          position: 1,
          parent_id: 0,
          image_url: '',
          category_icon_url: '',
          product_count: 0,
        },
        images: item.product.images,
        brand_name: '',
        brand_logo: '',
        base_image: item.product.images[0]?.url || null,
        thumb_image: item.product.images[0]?.url || null,
        s3_base_url: '',
        media_base_url: '',
        default_image: item.product.images[0]?.url || '',
        vendor_id: '1',
        vendor_name: 'Demo Vendor',
        mp_seller_id: '1',
        delivery_pincode: '',
        seller_shop_url: '',
        seller_code: 'DEMO',
        inventory_ids: [],
        postcode: '',
        product_images: item.product.images,
        base_total: item.base_total,
        total: item.total,
        tax_total: item.tax_total,
      },
      quantity: parseInt(item.quantity),
      base_total: item.base_total,
      total: item.total,
      tax_total: item.tax_total,
    }));
  };

  const refreshCart = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Refreshing cart from API...');
      const fetchedCartData = await CartService.getCart();
      console.log('Cart data fetched:', fetchedCartData);
      const cartItems = convertCartDataToCartItems(fetchedCartData);
      console.log('Converted cart items:', cartItems);
      setCart(cartItems);
      setCartData(fetchedCartData);

      // Save cart ID to localStorage
      if (fetchedCartData.id) {
        localStorage.setItem(CART_ID_KEY, fetchedCartData.id.toString());
        console.log('Saved cart ID to localStorage:', fetchedCartData.id);
      }
    } catch (err: any) {
      console.error('Error loading cart:', err);
      
      // For guest users or empty cart, don't show error - just keep empty cart
      if (err?.response?.status === 404 || err?.message?.includes('not found')) {
        setCart([]);
        setCartData(null);
        setError(null);
      } else {
        setError('Failed to load cart');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load cart from API on mount and when authentication changes
  useEffect(() => {
    // Always try to load cart from API (works for both guest and authenticated users)
    refreshCart();
  }, [isAuthenticated, refreshCart]);

  const addToCart = async (product: Product) => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Adding to cart - User authenticated:', isAuthenticated);

      // Prepare cart data for API
      const cartData: AddToCartRequest = {
        product_id: product.id,
        quantity: 1,
        shipping_type: 1, // Default shipping type
        seller_lat: '28.6139', // Default Delhi coordinates
        seller_lang: '77.2090',
        customer_pincode: '110001', // Default pincode
        seller_pincode: product.postcode || '110001',
        item_weight: product.weight || '1',
        moq: product.moq || '1',
        gst: product.gst || '18', // Use product GST, fallback to 18%
        shipCharge: 0,
        manufacturer_label: product.brand_label || product.brand_name || product.manufacturer_label || 'Generic',
      };

      const response = await CartService.addToCart(product.id, cartData);

      console.log('Add to cart API response:', response);

      // Check if API response status is false
      if (response.status === false) {
        toast({
          title: "Unable to add to cart",
          description: response.message || "The requested quantity is not available, please try again later.",
          variant: "destructive",
        });
        return;
      }

      // Update local cart with API response
      const cartItems = convertCartDataToCartItems(response.data.cart);
      console.log('Converted cart items after add:', cartItems);
      setCart(cartItems);
      setCartData(response.data.cart);

      // Save cart ID to localStorage for guest users
      if (response.data.cart.id) {
        localStorage.setItem(CART_ID_KEY, response.data.cart.id.toString());
        console.log('Saved cart ID to localStorage:', response.data.cart.id);
      }

      toast({
        title: "Added to cart",
        description: `${product.name} has been added to your cart.`,
      });
    } catch (err: any) {
      console.error('Error adding to cart:', err);
      setError('Failed to add item to cart');

      // Show appropriate error message
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to add item to cart. Please try again.';
      
      toast({
        title: "Unable to add to cart",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (productId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      // Find the cart item ID from local state
      const cartItem = cart.find(item => item.product.id === productId);
      if (!cartItem || !cartItem.id) {
        throw new Error('Cart item ID not found');
      }

      // Call API to remove item
      const updatedCart = await CartService.removeFromCart(cartItem.id);
      console.log('Remove from cart API response:', updatedCart);
      const cartItems = convertCartDataToCartItems(updatedCart);
      setCart(cartItems);
      setCartData(updatedCart);

      toast({
        title: "Removed from cart",
        description: "Item has been removed from your cart.",
      });
    } catch (err) {
      console.error('Error removing from cart:', err);
      setError('Failed to remove item from cart');

      // Fallback: Update local state only
      setCart((prevCart) => prevCart.filter((item) => item.product.id !== productId));

      toast({
        title: "Removed from cart",
        description: "Item has been removed from your local cart.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (productId: number, quantity: number) => {
    // If quantity is 0, remove the item from cart
    if (quantity === 0) {
      await removeFromCart(productId);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Find the cart item ID from local state
      const cartItem = cart.find(item => item.product.id === productId);
      if (!cartItem || !cartItem.id) {
        throw new Error('Cart item ID not found');
      }

      // Call API to update quantity
      const updatedCart = await CartService.updateCartItem(cartItem.id, quantity);
      console.log('Update quantity API response:', updatedCart);
      refreshCart();
      const cartItems = convertCartDataToCartItems(updatedCart);
      setCart(cartItems);
      setCartData(updatedCart);

      toast({
        title: "Quantity updated",
        description: "Cart quantity has been updated.",
      });
    } catch (err) {
      console.error('Error updating quantity:', err);
      setError('Failed to update item quantity');

      // Fallback: Update local state only
      setCart((prevCart) =>
        prevCart.map((item) =>
          item.product.id === productId ? { ...item, quantity } : item
        )
      );

      toast({
        title: "Quantity updated",
        description: "Cart quantity has been updated locally.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setIsLoading(true);
      setError(null);

      await CartService.clearCart();
      setCart([]);
      setCartData(null);

      // Clear cart ID from localStorage after successful order
      clearStoredCartId();
      console.log('Cleared cart ID from localStorage after order completion');

      toast({
        title: "Cart cleared",
        description: "All items have been removed from your cart.",
      });
    } catch (err) {
      console.error('Error clearing cart:', err);
      setError('Failed to clear cart');

      // Fallback to local state update
      setCart([]);

      // Clear cart ID even on error (cart is being cleared locally)
      clearStoredCartId();
      console.log('Cleared cart ID from localStorage (fallback)');

      toast({
        title: "Cart cleared (offline)",
        description: "All items have been removed from your cart. Will sync when online.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        cartData,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        isCartOpen,
        setIsCartOpen,
        isLoading,
        error,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export { CartContext, CartProvider };

// Export useCart hook from separate file for Fast Refresh compatibility
export { useCart } from './useCart';
